import { type ComponentProps, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Card, Input, PageLoader, Typography } from '../ui';
import { LottieLoader } from '../../LottieLoader';
import { useBasePath } from '../../../lib/use-base-path';
import { isRecipientId, useContactBook, useWholeContactBook, type ContactId, type WebContact } from '../../../lib/contact-book';
import { bookTail, isEmptyBook, matchesSearch, sectionsOf } from '../../../lib/contact-rows';
import { contactStage } from '../../../lib/outreach-stage';
import {
  contactLeadKey,
  contactLeadKeys,
  contactLeadNote,
  leadDisplayName,
  leadsSectionShown,
  matchLeadsToContacts,
  type PeopleLeadRow,
} from '../../../lib/profile-links-model';
import { ProfileLinkLeadsSection } from '../profile-links/ProfileLinkLeadsSection';
import { useDebounced } from '../../../lib/search';
import { useLoadBi } from '../../../lib/use-load-bi';
import { useSearchStartedBi } from '../../../lib/use-search-started-bi';
import { useTranslation } from '../../../lib/i18n';
import { ContactOutreachViewer } from './ContactOutreachViewer';
import { ContactRow } from './ContactRow';
import { ContactReadError } from './ContactReadError';
import { CategoryList, CategoryListGroupHeader } from '../category-view/CategoryView';
import styles from './contacts.module.css';
import { useCategoryRedesign } from '../../../lib/use-category-redesign';
import { ContactsBody as ContactsBodyLegacy } from './ContactsBody.legacy';

/** The Contacts-tab server search waits this long after the last keystroke — the same 300 ms both phones use. */
const CONTACT_SEARCH_DEBOUNCE_MS = 300;
/** How far ahead of the list's end the next book page is asked for — Android's 400 dp, as a root margin. */
const BOOK_PREFETCH_MARGIN = '400px';

/** What the list is currently showing, reported to a parent so it can place its own chrome (the web
 *  desktop toolbar's search + Add button belong beside the tabs, not in the list body). */
export type ContactsListState = 'loading' | 'error' | 'empty' | 'ready';

/**
 * The contacts list — a search, then a "Needs you" section (`contactStage === needsYou`) and an "All Contacts" section
 * (`sectionsOf`, the same split iOS and Android draw). No filter chips: the list is exactly the two
 * sections, per Figma.
 *
 * **Search ownership is per caller.** The standalone Outreach screen controls the query (`search` +
 * `onSearchChange`) so it can render the field in its desktop toolbar; the map-home embed passes
 * neither and the body owns an internal query. `renderSearch=false` lets the toolbar draw the field
 * while the body stays the data owner.
 *
 * **Written-down exception — pull-to-refresh is native-only here.** Both phones carry it because their
 * cache has no staleness; web's React Query cache refetches on its own past `staleTime`, and the
 * browser already offers reload. Adding a bespoke control only here would be the odd one out.
 */
function ContactsBodyRedesign({
  header,
  profileLeads,
  onOpen,
  emptyAction,
  illustratedEmpty,
  onListState,
  search: controlledSearch,
  onSearchChange,
  renderSearch = true,
  wholeBook = false,
}: {
  /**
   * List the WHOLE people book under the outreach-derived head, paged in as the reader scrolls, and
   * send the search to the server. The Outreach Contacts tab sets it; the map-home embed, which is
   * about exchanges, does not. Without it a user with people in their book but no outreach saw an
   * empty list while the agent told them the person was "already in your contacts".
   */
  wholeBook?: boolean;
  header?: ReactNode;
  /**
   * People's profile-link lead rows (`useProfileLinkLeadRows`). A lead matched to a contact this list holds
   * (`matchLeadsToContacts`) is shown on that contact's row; the rest are the "From your profile links" section,
   * drawn under `header` once the list's first read has settled (success or failure), and not under a search.
   */
  profileLeads?: readonly PeopleLeadRow[];
  /** Open the contact SCREEN by its identity — a person, or (address-only, #10660) a recipient. The
   *  list calls this for a contact with zero or many active conversations; a contact with exactly one
   *  opens that conversation in the viewer dialog instead (handled here). */
  onOpen: (id: ContactId) => void;
  /** The empty-state CTA rendered INSIDE the illustrated card (desktop). Narrow pins its own foot CTA
   *  instead, so it passes {@link illustratedEmpty} without this. */
  emptyAction?: ReactNode;
  /** Show the illustrated empty card (illustration + title + body) for an empty book rather than the
   *  plain empty line. The standalone Outreach screen sets it; the map-home embed does not. */
  illustratedEmpty?: boolean;
  /** Reports the current list state so a parent can place its own chrome (desktop toolbar / foot CTA). */
  onListState?: (state: ContactsListState) => void;
  /** Controlled search query. Omit for an internal one (the map-home embed). */
  search?: string;
  onSearchChange?: (value: string) => void;
  /** Whether the body renders the search field itself. Desktop web sets false — the toolbar draws it. */
  renderSearch?: boolean;
}) {
  const { t } = useTranslation();
  const basePath = useBasePath();
  // A contact with ONE conversation opens it here in the viewer; with several, the contact screen.
  // The viewer is keyed on the OUTREACH now, not the channel — so what opens is that conversation
  // rather than every outreach on its channel stitched into one scroll.
  const [viewer, setViewer] = useState<{ personId: string; recipientId?: string; outreachId: string } | null>(null);
  const openRow = (id: ContactId, outreachId?: string) => {
    if (!outreachId) return onOpen(id);
    setViewer(isRecipientId(id) ? { personId: '', recipientId: id.recipientId, outreachId } : { personId: id.personId, outreachId });
  };
  const [internalSearch, setInternalSearch] = useState('');
  const search = controlledSearch ?? internalSearch;
  const setSearch = onSearchChange ?? setInternalSearch;
  // Fire `outreach_search` once when the query goes blank→non-blank. Only the ACTIVE tab's body is
  // mounted, so watching the shared query here (and in InboxBody) reports exactly once — whether the
  // text came from this body's own field or the standalone screen's toolbar.
  useSearchStartedBi(search, 'contacts');

  // Two shapes of the same list. The Outreach Contacts tab (`wholeBook`) reads the browse AND the whole
  // people book under it, and sends its search to the SERVER, which resolves against that whole book —
  // a filter over the loaded pages could only ever find who has been scrolled to. The map-home embed
  // reads the browse alone and still filters it client-side (`matchesSearch`). Exactly one of the two
  // list reads is enabled, so neither surface reads the browse twice.
  const trimmed = search.trim();
  const searching = wholeBook && trimmed.length > 0;
  const debounced = useDebounced(trimmed, CONTACT_SEARCH_DEBOUNCE_MS);
  const whole = useWholeContactBook({ enabled: wholeBook });
  const plain = useContactBook('', { enabled: !wholeBook });
  const state = wholeBook ? whole : plain;
  const searched = useContactBook(debounced, {
    enabled: searching && debounced === trimmed,
    keepPreviousData: false,
  });
  // The answer for THIS query, or undefined while it is pending (the debounce counts as pending). A
  // result for an earlier query is never shown under a later one.
  const searchResults = searching && debounced === trimmed && searched.kind === 'ready' ? searched.contacts : undefined;
  const searchError = searching && debounced === trimmed && searched.kind === 'error' ? searched : undefined;

  // The head's order: every line is a person, and the LAST communication with them is the order.
  // Keyed on the contacts ARRAY, not the state object: both hooks build a fresh state every render,
  // and keying on it would re-sort the head (and, through it, the whole book tail) each keystroke.
  const headContacts = state.kind === 'ready' ? state.contacts : undefined;
  const entries = useMemo(() => {
    const list = headContacts ? [...headContacts] : [];
    const at = (e: WebContact) => (e.lastActivityAt ? new Date(e.lastActivityAt).getTime() || 0 : 0);
    return list.sort((a, b) => at(b) - at(a));
  }, [headContacts]);
  // The rest of the book, after the head, by name — the people the user has not written to yet.
  const book = whole.kind === 'ready' && wholeBook ? whole.book : undefined;
  const tail = useMemo(() => (book ? bookTail(entries, book) : []), [entries, book]);
  const filtered = useMemo(
    () => (wholeBook ? entries : entries.filter((entry) => matchesSearch(entry, search))),
    [wholeBook, entries, search],
  );
  // A search keeps the SERVER's ranking within each section; the browse keeps recency, and the tail
  // closes "All contacts" under no header of its own.
  const { needsAttention, all } = useMemo(() => {
    if (searching) return sectionsOf(searchResults ?? []);
    const split = sectionsOf(filtered);
    return { needsAttention: split.needsAttention, all: [...split.all, ...tail] };
  }, [searching, searchResults, filtered, tail]);

  // ── Profile-link leads who are already contacts ──
  // Matched against every contact LOADED — the head and the book pages so far. A lead whose person sits on a
  // book page not yet paged in stays in the section until that page arrives; the alternative, waiting on the
  // whole book, would hold the section back behind paging the reader never asked for.
  const leadMatch = useMemo(
    () => (profileLeads?.length ? matchLeadsToContacts(profileLeads, [...entries, ...tail].map(contactLeadKeys)) : undefined),
    [profileLeads, entries, tail],
  );
  // The row's lead note, unless its own line outranks it (`contactLeadNote`: needsYou / stuck keep theirs).
  const leadNoteFor = (entry: WebContact): string | undefined => {
    // The key alone (`contactLeadKey`) — building the full match keys per row per render is waste.
    const row = leadMatch?.byContact[contactLeadKey(entry)];
    const note = row ? contactLeadNote(row, contactStage(entry), new Date(), t('contacts.time.now')) : null;
    // No time (an unparseable `at`): the note ends at the link rather than in a lone "·" — as on both phones.
    return note ? t(note.key, note.params).replace(/\s*·\s*$/, '') : undefined;
  };
  // A folded-in lead names a row the book cannot: "Visitor from Wix 3.0", never "Unnamed contact".
  const unnamedLabelOf = (entry: WebContact): string | undefined => {
    const row = leadMatch?.byContact[contactLeadKey(entry)];
    return row ? leadDisplayName(row, (link) => t('profileLinks.leads.visitorFrom', { link }), t('profileLinks.main')) : undefined;
  };

  // The illustrated empty state is the empty BOOK (nobody at all, head or book) — never a no-results
  // search. Counting the head alone told a user with a full book and no outreach they had no contacts.
  // An unreadable book with nothing else to show is a failed read, not an empty book — see below.
  const bookUnreadable = wholeBook && whole.kind === 'ready' && whole.bookUnavailable && entries.length + tail.length === 0;
  const richEmpty =
    (illustratedEmpty || !!emptyAction) && !bookUnreadable && isEmptyBook(state.kind === 'ready', entries.length + tail.length, false);

  // ── Paging the book in ──
  // The next page is asked for when a sentinel under the list comes within BOOK_PREFETCH_MARGIN of the
  // viewport. It keeps reading while the sentinel stays in view (a page that adds nobody new moves
  // nothing, and a short page may leave it in view), and stops on a failure until the reader scrolls
  // away and back — a retry the reader drives, never a loop of its own. Same rules as both phones.
  const pageable = wholeBook && !searching && whole.kind === 'ready';
  const hasMore = pageable && whole.hasMore;
  const loadingMore = whole.kind === 'ready' && whole.loadingMore;
  const loadMoreFailed = whole.kind === 'ready' && whole.loadMoreFailed;
  const loadMore = whole.kind === 'ready' ? whole.loadMore : undefined;
  useLoadBi('contacts-book-page', { isLoading: loadingMore, isError: loadMoreFailed }, { enabled: wholeBook });
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const [sentinelInView, setSentinelInView] = useState(false);
  const loadMoreRef = useRef(loadMore);
  loadMoreRef.current = loadMore;
  useEffect(() => {
    const node = sentinelRef.current;
    if (!hasMore || !node || typeof IntersectionObserver === 'undefined') return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => {
        setSentinelInView(entry.isIntersecting);
        // Entering view is the reader asking — including after a failed page.
        if (entry.isIntersecting) loadMoreRef.current?.();
      },
      { rootMargin: BOOK_PREFETCH_MARGIN },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore]);
  const bookLength = book?.length ?? 0;
  useEffect(() => {
    // Still in view after a page landed: read on. A failure stops here until the sentinel re-enters.
    if (sentinelInView && hasMore && !loadingMore && !loadMoreFailed) loadMoreRef.current?.();
  }, [sentinelInView, hasMore, loadingMore, loadMoreFailed, bookLength]);

  const listState: ContactsListState =
    state.kind === 'loading' ? 'loading' : state.kind === 'error' || bookUnreadable ? 'error' : richEmpty ? 'empty' : 'ready';
  useEffect(() => {
    onListState?.(listState);
  }, [listState, onListState]);

  // The initial load hides the search too (matching both phones, whose chrome lives after the spinner).
  // Only the first load is `loading` — `keepPreviousData` keeps a search refetch in `ready`, so the
  // field never vanishes while the user is typing in it.
  const showSearch = renderSearch && state.kind !== 'loading' && !richEmpty;

  return (
    <div className={styles.body}>
      {header}
      {/* Once the list's first read has SETTLED — success or failure (`leadsSectionShown`): the leads never wait on
          the contacts succeeding, and with none loaded every lead is unmatched and shows here. */}
      {leadMatch && leadsSectionShown(state.kind !== 'loading', !!trimmed) ? <ProfileLinkLeadsSection rows={leadMatch.unmatched} /> : null}

      {showSearch ? (
        <div className={styles.search}>
          <Input
            kind="search"
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            onClear={() => setSearch('')}
            clearAriaLabel={t('search.dialog.clear')}
            placeholder={t('contacts.search.placeholder')}
          />
        </div>
      ) : null}

      {state.kind === 'loading' ? <PageLoader /> : null}

      {state.kind === 'error' ? (
        <ContactReadError canRetry onRetry={state.retry} detail={state.message} fullHeight={false} />
      ) : null}

      {richEmpty ? (
        <Card className={styles.emptyCard}>
          <img src={`${basePath}/illustrations/contacts-empty.svg`} alt="" className={styles.emptyIllustration} />
          <div className={styles.emptyText}>
            <Typography variant="h4" color="strong">
              {t('contacts.empty.title')}
            </Typography>
            <Typography variant="p3" color="secondary" className={styles.emptyBody}>
              {t('contacts.empty.body')}
            </Typography>
          </div>
          {emptyAction}
        </Card>
      ) : bookUnreadable && whole.kind === 'ready' && !searching ? (
        // The server could not read the book and there is nobody else on screen: the same failed read as
        // the list's own, with its Retry — never the "No contacts yet" an empty book would say.
        <ContactReadError canRetry onRetry={whole.retry} fullHeight={false} />
      ) : searchError ? (
        // A failed search is the same failed read as the list's own; Retry asks the CURRENT query again.
        <ContactReadError canRetry onRetry={searchError.retry} detail={searchError.message} fullHeight={false} />
      ) : searching && !searchResults ? (
        // This query's answer is pending (the debounce counts) — never the previous query's rows.
        <PageLoader />
      ) : state.kind === 'ready' && needsAttention.length + all.length === 0 ? (
        <div className={styles.empty}>
          <Typography variant="p2">
            {search ? t('contacts.empty.search.title') : t('contacts.empty.title')}
          </Typography>
          <Typography variant="p3" color="secondary">
            {search ? t('contacts.empty.search.body') : t('contacts.empty.body')}
          </Typography>
        </div>
      ) : null}

      {needsAttention.length + all.length > 0 ? (
        <CategoryList>
          {needsAttention.length > 0 ? (
            <CategoryListGroupHeader>{`${t('contacts.detail.needsYou')} · ${needsAttention.length}`}</CategoryListGroupHeader>
          ) : null}
          {needsAttention.map((entry) => (
            <ContactRow key={rowKey(entry)} entry={entry} onOpen={openRow} leadNote={leadNoteFor(entry)} unnamedLabel={unnamedLabelOf(entry)} />
          ))}
          {all.length > 0 ? <CategoryListGroupHeader>{`${t('contacts.section.all')} · ${all.length}`}</CategoryListGroupHeader> : null}
          {all.map((entry) => (
            <ContactRow key={rowKey(entry)} entry={entry} onOpen={openRow} leadNote={leadNoteFor(entry)} unnamedLabel={unnamedLabelOf(entry)} />
          ))}
        </CategoryList>
      ) : null}

      {/* The next book page: a sentinel the observer watches, and a compact, textless loader while a
          page is in flight — the rows above it are the content. */}
      {hasMore ? <div ref={sentinelRef} aria-hidden className={styles.bookSentinel} /> : null}
      {pageable && loadingMore ? (
        <div className={styles.bookLoadingMore}>
          <LottieLoader size={32} />
        </div>
      ) : null}

      {viewer ? (
        <ContactOutreachViewer
          personId={viewer.personId}
          recipientId={viewer.recipientId}
          outreachId={viewer.outreachId}
          open
          onClose={() => setViewer(null)}
          // Opened from the LIST, so the header opens the contact screen for the full record — by the
          // same identity (person or address-only recipient) the row carried.
          onOpenContact={() => {
            const id: ContactId = viewer.personId ? { personId: viewer.personId } : { recipientId: viewer.recipientId ?? '' };
            setViewer(null);
            onOpen(id);
          }}
        />
      ) : null}
    </div>
  );
}

/**
 * A stable key. Falls back to the newest outreach id for a counterparty with no Person behind them —
 * those rows have no person id, and keying every one on "" would make React treat two strangers as
 * the same row.
 */
function rowKey(entry: { contact?: { personId?: string }; outbound?: { outreachId: string }[]; inbound?: { outreachId: string }[] }): string {
  return entry.contact?.personId || entry.outbound?.[0]?.outreachId || entry.inbound?.[0]?.outreachId || '';
}

/** The redesign rides the activity-map home's release (`lib/use-category-redesign.ts`); off, this is the legacy design. */
export function ContactsBody(props: ComponentProps<typeof ContactsBodyRedesign>) {
  const redesign = useCategoryRedesign();
  if (!redesign.resolved) return null;
  return redesign.on ? <ContactsBodyRedesign {...props} /> : <ContactsBodyLegacy {...props} />;
}
